import { TransportMode } from './provider.types';
import { GeoObjectType } from './geo-object.types';

// ==================== ENUMS ====================

export enum CoverageDirection {
  import = 'import',
  export = 'export',
  domestic = 'domestic',
}

// ==================== COUNTRY COVERAGE TYPES ====================

/**
 * Direction settings within a country coverage
 */
export interface CoverageDirectionData {
  direction: CoverageDirection;
  isFeatured: boolean;
  transportModes: TransportMode[];
}

/**
 * Region inclusion/exclusion within a country coverage
 */
export interface CoverageRegionData {
  geoObjectId: string;
  isFeatured: boolean;
  isExcluded: boolean;
}

/**
 * Port inclusion/exclusion within a country coverage
 */
export interface CoveragePortData {
  geoObjectId: string;
  isFeatured: boolean;
  isExcluded: boolean;
}

/**
 * Create a new country coverage
 */
export interface CreateCountryCoverageData {
  geoObjectId: string; // Must be adm0 (country)
  isFeatured?: boolean;
  directions?: CoverageDirectionData[];
}

/**
 * Update country coverage
 */
export interface UpdateCountryCoverageData {
  isFeatured?: boolean;
}

/**
 * Update directions for a country coverage
 */
export interface UpdateCoverageDirectionsData {
  directions: CoverageDirectionData[];
}

/**
 * Update regions for a country coverage
 */
export interface UpdateCoverageRegionsData {
  regions: CoverageRegionData[];
}

/**
 * Update ports for a country coverage
 */
export interface UpdateCoveragePortsData {
  ports: CoveragePortData[];
}

/**
 * Direction response DTO
 */
export interface CoverageDirectionResponseDto {
  id: string;
  countryCoverageId: string;
  direction: CoverageDirection;
  isFeatured: boolean;
  transportModes: TransportMode[];
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Region response DTO
 */
export interface CoverageRegionResponseDto {
  id: string;
  countryCoverageId: string;
  geoObjectId: string;
  isFeatured: boolean;
  isExcluded: boolean;
  createdAt: Date;
  updatedAt: Date;
  geoObject: {
    id: string;
    name: string;
    type: GeoObjectType;
    isoSubdivisionCode: string | null;
  };
}

/**
 * Port response DTO
 */
export interface CoveragePortResponseDto {
  id: string;
  countryCoverageId: string;
  geoObjectId: string;
  isFeatured: boolean;
  isExcluded: boolean;
  createdAt: Date;
  updatedAt: Date;
  geoObject: {
    id: string;
    name: string;
    type: GeoObjectType;
    unlocode: string | null;
    regionId: string | null;
  };
}

/**
 * Country coverage response DTO
 */
export interface CountryCoverageResponseDto {
  id: string;
  providerId: string;
  geoObjectId: string;
  isFeatured: boolean;
  createdAt: Date;
  updatedAt: Date;
  geoObject: {
    id: string;
    name: string;
    type: GeoObjectType;
    isoCountryCode: string | null;
    adminLevel: string | null;
  };
  directions: CoverageDirectionResponseDto[];
  regions: CoverageRegionResponseDto[];
  ports: CoveragePortResponseDto[];
}

/**
 * Summary DTO for public pages
 */
export interface CountryCoverageSummaryDto {
  countryId: string;
  countryName: string;
  isoCountryCode: string | null;
  isFeatured: boolean;
  directions: {
    direction: CoverageDirection;
    isFeatured: boolean;
  }[];
  featuredRegionNames: string[];
  excludedRegionNames: string[];
}

// ==================== LEGACY COVERAGE AREA TYPES (deprecated) ====================

export interface CreateCoverageAreaData {
  geoObjectId: string;
  transportMode: TransportMode;
  isSpecialization?: boolean;
  notes?: string;
}

export interface UpdateCoverageAreaData {
  isSpecialization?: boolean;
  notes?: string;
}

export interface CoverageAreaResponseDto {
  id: string;
  providerId: string;
  geoObjectId: string;
  transportMode: TransportMode;
  isSpecialization: boolean;
  notes: string | null;
  createdAt: Date;
  updatedAt: Date;
  geoObject: {
    id: string;
    name: string;
    type: GeoObjectType;
    isoCountryCode: string | null;
    unlocode: string | null;
    latitude: number | null;
    longitude: number | null;
    bboxMinLat: number | null;
    bboxMaxLat: number | null;
    bboxMinLng: number | null;
    bboxMaxLng: number | null;
  };
}

export interface CoverageAreaSummaryDto {
  geoObjectId: string;
  name: string;
  type: GeoObjectType;
  transportMode: TransportMode;
  isSpecialization: boolean;
  coordinates: [number, number] | null;
  bbox: [number, number, number, number] | null;
}

// ==================== ROUTE TYPES ====================

export interface CreateRouteData {
  originGeoObjectId: string;      // trade_regions, adm0 (country), or adm1 (region)
  destinationGeoObjectId: string; // trade_regions, adm0 (country), or adm1 (region)
  transportMode: TransportMode;
  isFeatured?: boolean;
  createReverseRoute?: boolean;
}

export interface UpdateRouteData {
  originGeoObjectId?: string;
  destinationGeoObjectId?: string;
  transportMode?: TransportMode;
  isFeatured?: boolean;
}

export interface RouteResponseDto {
  id: string;
  providerId: string;
  originGeoObjectId: string;
  destinationGeoObjectId: string;
  transportMode: TransportMode;
  isFeatured: boolean;
  createdAt: Date;
  updatedAt: Date;
  originGeoObject: {
    id: string;
    name: string;
    type: GeoObjectType;
    adminLevel: string | null;
    isoCountryCode: string | null;
    latitude: number | null;
    longitude: number | null;
  };
  destinationGeoObject: {
    id: string;
    name: string;
    type: GeoObjectType;
    adminLevel: string | null;
    isoCountryCode: string | null;
    latitude: number | null;
    longitude: number | null;
  };
}

export interface RouteSummaryDto {
  id: string;
  origin: {
    name: string;
  };
  destination: {
    name: string;
  };
  transportMode: TransportMode;
  isFeatured: boolean;
}

// ==================== OFFICE TYPES ====================

/**
 * Create an office with address fields
 */
export interface CreateOfficeData {
  // Office identification
  label?: string;

  // Full address fields
  addressLine1?: string;
  addressLine2?: string;
  postalCode?: string;
  cityName?: string;
  stateRegion?: string;

  // Links to reference geo data (countryId is required)
  countryId: string;
  placeId?: string;

  // Coordinates (from geocoding)
  latitude?: number;
  longitude?: number;

  // Optional OSM reference for the exact address
  osmId?: number;
  osmType?: 'node' | 'way' | 'relation';

  // Original raw address input (from AI parser, line breaks replaced with commas)
  addressOriginal?: string;

  // Metadata
  isHeadquarters?: boolean;
}

/**
 * Create an office with full location data (from map picker/geocoding)
 */
export interface CreateOfficeWithLocationData {
  // Office identification
  label?: string;

  // Full address fields
  addressLine1?: string;
  addressLine2?: string;
  postalCode?: string;
  cityName?: string;
  stateRegion?: string;

  // Country is required (ISO code)
  countryCode: string;

  // Pre-resolved geo object IDs from frontend (used as fallback)
  regionId?: string;

  // Coordinates (from geocoding)
  latitude: number;
  longitude: number;

  // Mapbox reference (primary geocoding provider)
  mapboxId?: string;

  // Legacy OSM reference (for backward compatibility)
  osmId?: number;
  osmType?: 'node' | 'way' | 'relation';

  // Display name from geocoding
  displayName?: string;

  // Original raw address input (from AI parser, line breaks replaced with commas)
  addressOriginal?: string;

  // Raw geocoding response (from Mapbox or other providers)
  geocodingResponse?: object;

  // Metadata
  isHeadquarters?: boolean;
}

export interface UpdateOfficeData {
  label?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  cityName?: string;
  stateRegion?: string;
  countryId?: string;
  placeId?: string | null;
  latitude?: number;
  longitude?: number;
  addressOriginal?: string;
  isHeadquarters?: boolean;
  isActive?: boolean;
}

export interface OfficeResponseDto {
  id: string;
  providerId: string;
  label: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  cityName: string | null;
  stateRegion: string | null;
  countryId: string;
  placeId: string | null;
  latitude: number | null;
  longitude: number | null;
  addressOriginal: string | null;
  isHeadquarters: boolean;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  country: {
    id: string;
    name: string;
    isoCountryCode: string | null;
  };
  place: {
    id: string;
    name: string;
    type: GeoObjectType;
    regionId: string | null;
    region: { id: string; name: string } | null;
  } | null;
}

export interface OfficeSummaryDto {
  id: string;
  label: string | null;
  cityName: string | null;
  countryName: string | null;
  isoCountryCode: string | null;
  coordinates: [number, number] | null;
  isHeadquarters: boolean;
}
